import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  /** Say what to do next, not merely that there is nothing here. */
  description?: string;
  action?: ReactNode;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Shown wherever a collection is legitimately empty.
 *
 * An empty state is a design surface, not an oversight: "No boards yet" plus a
 * "Create board" button is the difference between a new staff member getting
 * started and filing a support request.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  size = 'md',
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 text-center',
        size === 'md' ? 'px-6 py-14' : 'px-4 py-8',
        className,
      )}
    >
      {icon ? (
        <span
          aria-hidden="true"
          className={cn(
            'flex items-center justify-center rounded-full bg-surface-sunken text-text-tertiary',
            size === 'md' ? 'size-12 [&_svg]:size-6' : 'size-10 [&_svg]:size-5',
          )}
        >
          {icon}
        </span>
      ) : null}

      <div className="flex flex-col gap-1">
        <h3
          className={cn('font-semibold text-text-primary', size === 'md' ? 'text-base' : 'text-sm')}
        >
          {title}
        </h3>
        {description ? <p className="max-w-sm text-sm text-text-secondary">{description}</p> : null}
      </div>

      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}
